// src/documents/xlsx/adaptYCDataToXlsx.js
//
// generateYCDataFromKolonConfig (ya da transformSimpleSupplyToYC_Data) çıktısını
// generateYuklemeCetveliXlsx'in beklediği şekle çevirir.
//
// Kaynak panel objesi:
//   { name, id, installedPower, demandPower, demandFactor, current,
//     panelMainBreaker: { curve, current, polar, shortCircuitCurCap, type },
//     branchCircuits: [
//       { type, sorti, totalPower, phaseName: 'r'|'s'|'t'|'rst',
//         phaseCount, description,
//         breaker: { curve, current, polar, shortCircuitCurCap, type } }
//     ],
//     ... }

/** Ana sigorta gösterimi: { curve:'B', polar:3, current:25 } → "B-3x25A" */
function formatMainBreaker(b) {
  if (!b) return '';
  const curve = b.curve || 'B';
  const polar = b.polar || 1;
  const cur   = b.current ?? 0;
  return polar > 1 ? `${curve}-${polar}x${cur}A` : `${curve}-${cur}A`;
}

/** Branch sigorta gösterimi: { curve:'B', current:10 } → "B-10A" */
function formatBranchBreaker(b) {
  if (!b) return '';
  const curve = b.curve || 'B';
  const cur   = b.current ?? 0;
  // Branch breaker'lar genelde tek kutuplu; çift/üç kutuplu olanlar "B-3x16A" gibi.
  if (b.polar && b.polar > 1) return `${curve}-${b.polar}x${cur}A`;
  return `${curve}-${cur}A`;
}

/** Kısa devre kapasitesi: 3 → "3kA" */
function formatShortCircuit(kA) {
  if (kA == null) return '';
  return `${kA}kA`;
}

/** "r" / "s" / "t" / "rst" → "R" / "S" / "T" / "RST" */
function normalizePhase(p) {
  return (p || '').toString().toUpperCase();
}

/** Branch tipinden sortı sütununu çıkar (light → IŞIK, diğerleri → PRİZ). */
function isLightingBranch(branch) {
  const t = (branch.type || '').toLowerCase();
  return t === 'light' || t === 'lighting' || t === 'wirelighting';
}

function adaptBranch(br) {
  const phase = normalizePhase(br.phaseName || br.phase);
  const power = br.totalPower ?? br.power ?? 0;
  const out = {
    breakerTitle:      formatBranchBreaker(br.breaker),
    shortCircuitTitle: formatShortCircuit(br.breaker?.shortCircuitCurCap),
    phase,
    phasePower:        power,
    totalPower:        power,
    description:       br.description || '',
  };
  if (isLightingBranch(br)) out.lightCount = br.sorti || 0;
  else                      out.plugCount  = br.sorti || 0;
  return out;
}

function adaptPanel(panel) {
  return {
    id:                       panel.id,
    name:                     panel.name || '',
    subscribeType:            panel.subscribeType || '',  // TGH kategorizasyonu için (Mesken/Asansör/İşYeri/Müşterek)
    installedPower:           panel.installedPower ?? 0,
    demandPower:              panel.demandPower ?? 0,
    demandFactor:             panel.demandFactor || '60-40',
    cableTitle:               panel.cableTitle || '',
    current:                  parseFloat(panel.current) || panel.current || 0,
    mainBreakerTitle:         formatMainBreaker(panel.panelMainBreaker),
    cableCurrentLimit:        panel.cableCurrentLimit ?? 0,
    // Alt tablo için besleyici (sayaç pano → panel) bilgileri — kaynakta yoksa
    // ana sigorta bilgisini kullan, kullanıcı sonra inceleyip düzeltebilir.
    feederCableTitle:         panel.feederCableTitle  || panel.cableTitle || '',
    feederBreakerTitle:       panel.feederBreakerTitle || formatMainBreaker(panel.panelMainBreaker),
    feederShortCircuitTitle:  panel.feederShortCircuitTitle ||
                              formatShortCircuit(panel.panelMainBreaker?.shortCircuitCurCap),
    feederCableCurrentLimit:  panel.feederCableCurrentLimit ?? panel.cableCurrentLimit ?? 0,
    feederCurrent:            parseFloat(panel.feederCurrent) || panel.feederCurrent ||
                              parseFloat(panel.current) || panel.current || 0,
    lowerTableNote:           panel.lowerTableNote || `${panel.name || 'Panel'} Tali Tablo`,
    branchCircuits:           (panel.branchCircuits || []).map(adaptBranch),
  };
}

/**
 * @param {Array|Object} ycData - generateYCDataFromKolonConfig çıktısı (array)
 *                                veya transformSimpleSupplyToYC_Data ({panels, adp}).
 * @returns {{panels: Array}} generateYuklemeCetveliXlsx'e doğrudan verilebilir.
 */
export function adaptYCDataToXlsx(ycData) {
  const panels = Array.isArray(ycData) ? ycData : (ycData?.panels || []);
  // ADP (MainPanel): generator TOPLAM satırında kolon/kesici/ATK için kullanır.
  // earthCurCap'li cableCurrentLimit zaten transformSimpleSupplyToYC_Data'da set
  // edilmiş (MainPanel için toprak kapasitesi kuralı).
  const adp = Array.isArray(ycData) ? null : (ycData?.adp || null);
  return { panels: panels.map(adaptPanel), adp };
}

export default adaptYCDataToXlsx;
